import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const WORD = "SUPPRIMER";

export function DeleteButton({ url, params, label, description, onDeleted, testId, children, className, variant = "ghost" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState(null);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  if (user?.role !== "admin") return null;

  const close = () => { setOpen(false); setReasons(null); setWord(""); };
  const run = async (confirm) => {
    setBusy(true);
    try {
      await api.delete(url, { params: { ...(params || {}), ...(confirm ? { confirm } : {}) } });
      toast.success(`${label} supprimé`);
      close();
      onDeleted?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.requires_confirmation) setReasons(d.reasons || []);
      else toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button type="button" variant={variant} size={children ? "sm" : "icon"} onClick={(e) => { e.stopPropagation(); setOpen(true); }} className={cn(children ? "rounded-full text-red-500 hover:bg-red-500/10 hover:text-red-500" : "h-8 w-8 shrink-0 text-muted-foreground hover:bg-red-500/10 hover:text-red-500", className)} aria-label={`Supprimer ${label}`} data-testid={testId}>
        <Trash2 className={cn("h-4 w-4", children && "mr-1")} />{children}
      </Button>
      <AlertDialog open={open} onOpenChange={(v) => !v && close()}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()} data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{reasons ? "Confirmation renforcée" : `Supprimer ${label} ?`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                {reasons ? (
                  <>
                    <p>Cette suppression touche des éléments sensibles :</p>
                    <ul className="list-disc pl-5 text-amber-500">{reasons.map((r) => <li key={r}>{r}</li>)}</ul>
                    <p>Pour confirmer, saisissez <span className="font-mono font-bold text-foreground">{WORD}</span> ci-dessous.</p>
                    <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder={WORD} className="font-mono" autoFocus data-testid="delete-confirm-word" />
                  </>
                ) : <p>{description || "Cette action est définitive et ne peut pas être annulée."}</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Annuler</AlertDialogCancel>
            <Button variant="destructive" disabled={busy || (reasons && word !== WORD)} onClick={() => run(reasons ? word : undefined)} data-testid="delete-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Supprimer définitivement"}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
